import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { RootState } from 'store';
import { ThemeProvider } from 'styled-components';
import { Theme } from 'theme/theme';

import useIsDesktop from 'hooks/useIsDesktop';

import DesktopHeader from './DesktopHeader';
import DesktopMenu from './DesktopMenu';
import { MenuContentContainer, Content } from './styles';
import { loadPersonalizedFiles } from 'services/personalizedScripts';
import { getFavicon } from 'services/photoFaviconService';
import { base64ToFile } from 'utils/generic';

interface Props {
  children: JSX.Element;
}

const NO_DESKTOP_ROUTES = [
  /^\/corporate\/invite/,
  /^\/corporate\/registration/,
  /^\/corporate\/login/,
  /^\/$/,
  /^\/corporate\/invite\/token\//,
  /^\/corporate\/welcome\/?$/,
  /^\/corporate\/adminonboarding\/?$/,
  /^\/corporate\/createreseller/,
  /^\/corporate\/createresellerform/,
];

const ApplicationLayout = ({ children }: Props) => {
  const history = useHistory();

  const isDesktop = useIsDesktop();
  const {
    location: { pathname },
  } = history;
  const shouldShowDesktopLayout = isDesktop && !NO_DESKTOP_ROUTES.some((route) => route.test(pathname));

  const companyConfiguration = useSelector((state: RootState) => state.companyConfiguration.configuration);

  const overrideTheme = () => {
    const theme = { ...Theme };

    const primaryBackgroundColor = companyConfiguration?.primaryBackgroundColor || '#F4BF31';
    const primaryColor = companyConfiguration?.primaryColor || '#FFFFFF';
    const secondaryBackgroundColor = companyConfiguration?.secondaryBackgroundColor || '#F9CD45';
    const secondaryColor = companyConfiguration?.secondaryColor || '#444444';

    theme.primaryBackgroundColor = primaryBackgroundColor;
    theme.secondaryBackgroundColor = secondaryBackgroundColor;
    theme.secondaryColor = secondaryColor;
    theme.primaryColor = primaryColor;
    theme.desktop.header.color = primaryBackgroundColor; //iconos header desktop
    theme.floatingActionButton.background = primaryBackgroundColor; //boton flotante '+'
    theme.notFound.header = primaryBackgroundColor; // "Oops" el texto de no encontrar resultados en los autocompletes

    theme.flightLine.line = secondaryBackgroundColor; // linea del vuelo en card
    theme.flightLine.point = primaryBackgroundColor; //punto del vuelo en el card
    theme.switch.active.background = primaryBackgroundColor; //color de fondo del tab de busqueda activo
    theme.switch.active.color = primaryColor; // color del fuente de tab de busqueda activo
    theme.switch.inactive.background = secondaryBackgroundColor; //color de fondo del tab de busqueda activo
    theme.switch.inactive.color = secondaryColor; // color del fuente de tab de busqueda activo
    theme.switchResults.active.background = primaryBackgroundColor; //color de fondo del tab de busqueda activo
    theme.switchResults.active.color = primaryColor; // color del fuente de tab de busqueda activo
    theme.switchResults.inactive.background = secondaryBackgroundColor; //color de fondo del tab de busqueda activo
    theme.switchResults.inactive.color = secondaryColor; // color del fuente de tab de busqueda activo
    theme.button.background = primaryBackgroundColor; //botones generales
    theme.button.color = primaryColor; //botones generales
    theme.button.invertedBackground = secondaryBackgroundColor; //botones generales
    theme.button.invertedColor = secondaryColor; //botones generales
    theme.checkbox.checked = primaryBackgroundColor; // checkbox
    theme.checkbox.border = secondaryBackgroundColor; //borda del checkbox, independiente de activo o desactivado
    theme.checkbox.disabled = secondaryBackgroundColor; //checkbox desabilitado
    theme.checkbox.empty = secondaryBackgroundColor; //checkbox no seleccionado
    theme.desktop.menu.active.backgroundIcon = primaryBackgroundColor;
    theme.desktop.menu.active.backgroundText = secondaryBackgroundColor;
    theme.desktop.menu.active.colorText = primaryColor;
    theme.desktop.menu.general.colorText = secondaryColor;
    theme.flightSectorBaggage.includeLuggage = primaryBackgroundColor;
    return theme;
  };

  useEffect(() => {
    document.title = companyConfiguration?.pageTitle || 'NetViajes Corporate';
    changeFavicon('/images/favicon-netviaejs.ico');

    if (companyConfiguration?.favicon) {
      getAndChangeFavicon();      
    }
    
  }, []);

  const getAndChangeFavicon = useCallback(
    async () => {
      try {

        try {
          
          const responsePhoto = await getFavicon(companyConfiguration?.companyId);

          if (responsePhoto.succeeded && responsePhoto.data.picture) {
            const file = await base64ToFile(
              `data:text/plain;base64,${responsePhoto.data.picture}` as string,
              responsePhoto.data.extension,
            );
            const reader = new FileReader();
            reader.readAsDataURL(file as File);
            reader.onload = () => {
              changeFavicon(reader.result as string);
            };
          }
        } catch (e) {}
      } catch (error) { } 
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  
  useEffect(() => {
    loadPersonalizedFiles(companyConfiguration?.stylesPersonalization?.cssUrl, companyConfiguration?.stylesPersonalization?.javascriptUrl);
  }, [companyConfiguration]);

  const changeFavicon = (src) => {
    const link = document.createElement('link'),
      oldLink = document.getElementById('dynamic-favicon');
    link.id = 'dynamic-favicon';
    link.rel = 'shortcut icon';
    link.href = src;
    if (oldLink) {
      document.head.removeChild(oldLink);
    }
    document.head.appendChild(link);
  }

  return shouldShowDesktopLayout ? (
    <>
      <ThemeProvider theme={overrideTheme()}>
        <DesktopHeader />
        <MenuContentContainer>
          <DesktopMenu />
          <Content className='ContentBody'>{children}</Content>
        </MenuContentContainer>
      </ThemeProvider>
    </>
  ) : (
    <ThemeProvider theme={overrideTheme()}>{children}</ThemeProvider>
  );
};

export default ApplicationLayout;
